import {
  useState,
  useEffect,
  useCallback,
  useRef,
  createContext,
  useContext,
  ReactNode,
} from 'react';
import { Capacitor } from '@capacitor/core';
import { DeviceFlags } from '@/plugins/device-flags';

/**
 * Unified Required-Permissions gate.
 *
 * Drives the full-screen RequiredPermissionsScreen and stays mounted above
 * the router so the screen instantly re-replaces any active route the
 * moment a permission is revoked or Developer Options are turned on.
 *
 * Android: enforces 3 permissions:
 *   1. Location  — runtime perm AND system GPS toggle
 *   2. Battery   — PowerManager.isIgnoringBatteryOptimizations
 *   3. Developer — Settings.Global.DEVELOPMENT_SETTINGS_ENABLED == 0
 *
 * Web/iOS: only the location permission is meaningful — others auto-resolve
 * to `granted`.
 */

export type LocationPermState = 'granted' | 'foreground_only' | 'prompt' | 'denied' | 'gps_off';
export type BinaryPermState = 'granted' | 'missing';

export interface PermissionsState {
  location: LocationPermState;
  battery: BinaryPermState;
  developer: BinaryPermState; // 'granted' === Dev Options OFF
  fullScreen: BinaryPermState; // 'granted' === full-screen-intent allowed (Android 14+)
  oemAutostart: BinaryPermState; // 'granted' === user confirmed OEM Autostart, or device is not OEM
}

export type PermissionKey = keyof PermissionsState;
export type GateStatus = 'checking' | 'granted' | 'missing';

export type LocationStep = 'idle' | 'await-rationale';

interface ContextValue {
  status: GateStatus;
  perms: PermissionsState;
  request: (key: PermissionKey) => Promise<void>;
  revalidate: () => Promise<void>;
  locationStep: LocationStep;
  confirmBackgroundRationale: () => Promise<void>;
}

const DEFAULT_PERMS: PermissionsState = {
  location: 'granted',
  battery: 'granted',
  developer: 'granted',
  fullScreen: 'granted',
  oemAutostart: 'granted',
};

const PermissionsGateContext = createContext<ContextValue>({
  status: 'granted',
  perms: DEFAULT_PERMS,
  request: async () => {},
  revalidate: async () => {},
  locationStep: 'idle',
  confirmBackgroundRationale: async () => {},
});

export function usePermissionsGate() {
  return useContext(PermissionsGateContext);
}

function deriveStatus(p: PermissionsState): GateStatus {
  if (
    p.location === 'granted' &&
    p.battery === 'granted' &&
    p.developer === 'granted' &&
    p.fullScreen === 'granted' &&
    p.oemAutostart === 'granted'
  ) {
    return 'granted';
  }
  return 'missing';
}

export function PermissionsGateProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<GateStatus>('checking');
  const [perms, setPerms] = useState<PermissionsState>({
    location: 'prompt',
    battery: 'missing',
    developer: 'granted',
    fullScreen: 'granted',
    oemAutostart: 'granted',
  });
  const [locationStep, setLocationStep] = useState<LocationStep>('idle');

  const isAndroidRef = useRef(false);
  const isNativeRef = useRef(false);
  const cancelledRef = useRef(false);
  const requestingRef = useRef(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const evaluate = useCallback(async () => {
    if (cancelledRef.current) return;

    // Web / iOS: only the location bit matters; rest = granted
    if (!isAndroidRef.current) {
      let loc: LocationPermState = 'granted';
      try {
        if (isNativeRef.current) {
          const { Geolocation } = await import('@capacitor/geolocation');
          const r = await Geolocation.checkPermissions();
          if (r.location === 'denied') loc = 'denied';
          else if (r.location === 'prompt' || (r.location as string) === 'prompt-with-rationale') loc = 'prompt';
          else loc = 'granted';
        } else if (typeof navigator !== 'undefined' && 'permissions' in navigator) {
          const p = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
          if (p.state === 'denied') loc = 'denied';
          else if (p.state === 'prompt') loc = 'prompt';
          else loc = 'granted';
        }
      } catch {
        loc = 'prompt'; // fail closed
      }
      if (cancelledRef.current) return;
      const next: PermissionsState = {
        location: loc,
        battery: 'granted',
        developer: 'granted',
        fullScreen: 'granted',
        oemAutostart: 'granted',
      };
      setPerms(next);
      setStatus(deriveStatus(next));
      return;
    }

    // Android: read all four signals.
    const next: PermissionsState = { ...DEFAULT_PERMS };
    try {
      const { Geolocation } = await import('@capacitor/geolocation');
      const r = await Geolocation.checkPermissions();
      if (r.location === 'denied') next.location = 'denied';
      else if (r.location === 'prompt' || (r.location as string) === 'prompt-with-rationale') next.location = 'prompt';
      else {
        try {
          const { enabled } = await DeviceFlags.isLocationEnabled();
          if (!enabled) {
            next.location = 'gps_off';
          } else {
            // Foreground granted — verify ACCESS_BACKGROUND_LOCATION.
            try {
              const { enabled: bg } = await DeviceFlags.hasBackgroundLocationPermission();
              next.location = bg ? 'granted' : 'foreground_only';
            } catch {
              next.location = 'foreground_only';
            }
          }
        } catch {
          next.location = 'prompt';
        }
      }
    } catch {
      next.location = 'prompt';
    }

    try {
      const { enabled } = await DeviceFlags.isIgnoringBatteryOptimizations();
      next.battery = enabled ? 'granted' : 'missing';
    } catch {
      next.battery = 'missing';
    }

    try {
      // Skip developer-mode check in DEV builds so USB debugging doesn't
      // block testing — set VITE_SKIP_DEV_MODE_CHECK=true in .env.local.
      const skipDevCheck = import.meta.env.VITE_SKIP_DEV_MODE_CHECK === 'true';
      if (skipDevCheck) {
        next.developer = 'granted';
      } else {
        const { developerMode } = await DeviceFlags.getFlags();
        next.developer = developerMode ? 'missing' : 'granted';
      }
    } catch {
      next.developer = 'granted'; // fail open here — don't lock out on plugin error
    }

    try {
      const { allowed } = await DeviceFlags.canUseFullScreenIntent();
      next.fullScreen = allowed ? 'granted' : 'missing';
    } catch {
      next.fullScreen = 'missing'; // fail CLOSED — required for trip offers
    }

    try {
      const { required } = await DeviceFlags.isOemAutoStartRequired();
      if (!required) {
        next.oemAutostart = 'granted';
      } else {
        const { confirmed } = await DeviceFlags.isOemAutoStartConfirmed();
        next.oemAutostart = confirmed ? 'granted' : 'missing';
      }
    } catch {
      next.oemAutostart = 'granted'; // fail open — never block non-OEM Pixel/Samsung
    }

    if (cancelledRef.current) return;
    setPerms(next);
    setStatus(deriveStatus(next));
  }, []);

  // ─── Per-card request handler ───────────────────────────────────────
  const request = useCallback(async (key: PermissionKey) => {
    if (requestingRef.current) return;
    requestingRef.current = true;
    try {
      if (key === 'location') {
        if (isAndroidRef.current) {
          // Strict ordering: GPS toggle must be ON before runtime dialog
          // so the precise/approximate chooser doesn't leak onto Home.
          try {
            const { enabled } = await DeviceFlags.isLocationEnabled();
            if (!enabled) {
              try { await DeviceFlags.openLocationSettings(); } catch {}
              await new Promise<void>((resolve) => {
                let settled = false;
                const t = setTimeout(() => { if (!settled) { settled = true; try { handle.then(h => h.remove()); } catch {} resolve(); } }, 30_000);
                const handle = DeviceFlags.addListener('locationStateChanged', ({ enabled: en }) => {
                  if (en && !settled) { settled = true; clearTimeout(t); try { handle.then(h => h.remove()); } catch {} resolve(); }
                });
              });
            }
          } catch {}
          // STEP 1 — fire foreground runtime prompt if needed.
          try {
            const { Geolocation } = await import('@capacitor/geolocation');
            const r = await Geolocation.checkPermissions();
            if (r.location === 'prompt' || (r.location as string) === 'prompt-with-rationale') {
              await Geolocation.requestPermissions();
            }
          } catch {}
          // STEP 2 — re-check foreground; if granted but background missing,
          // open the rationale sheet (the OK button drives STEP 3).
          try {
            const { Geolocation } = await import('@capacitor/geolocation');
            const r2 = await Geolocation.checkPermissions();
            if (r2.location === 'granted') {
              let bg = false;
              try {
                const res = await DeviceFlags.hasBackgroundLocationPermission();
                bg = !!res.enabled;
              } catch {}
              if (!bg) {
                setLocationStep('await-rationale');
              }
            } else {
              // Foreground denied — bounce them to system app settings.
              try { await DeviceFlags.openAppLocationSettings(); } catch {}
            }
          } catch {}
        } else if (typeof navigator !== 'undefined' && 'geolocation' in navigator) {
          await new Promise<void>((resolve) => {
            navigator.geolocation.getCurrentPosition(() => resolve(), () => resolve(), { timeout: 10000 });
          });
        }
      } else if (key === 'battery') {
        try { await DeviceFlags.requestIgnoreBatteryOptimizations(); } catch {}
      } else if (key === 'developer') {
        try { await DeviceFlags.openDeveloperSettings(); } catch {}
      } else if (key === 'fullScreen') {
        try { await DeviceFlags.openFullScreenIntentSettings(); } catch {}
      } else if (key === 'oemAutostart') {
        try { await DeviceFlags.openOemAutoStartSettings(); } catch {}
        // The OEM has no public API to verify the toggle — record user
        // confirmation when they return to the app. Re-opening if they
        // didn't actually enable it is handled by the user tapping again.
        try { await DeviceFlags.markOemAutoStartConfirmed(); } catch {}
      }
      await evaluate();
    } finally {
      requestingRef.current = false;
    }
  }, [evaluate]);

  // STEP 3 — fired by the rationale sheet's OK button. Opens the system
  // app-info Location page where "Allow all the time" lives.
  const confirmBackgroundRationale = useCallback(async () => {
    setLocationStep('idle');
    if (!isAndroidRef.current) return;
    // Fire the native runtime prompt — on Android 11+ this routes to the
    // system per-app Location screen (with "Allow all the time"). On
    // Android 10 a runtime dialog appears with the same option.
    let stayedVisible = true;
    const onVis = () => { if (document.hidden) stayedVisible = false; };
    document.addEventListener('visibilitychange', onVis);
    try { await DeviceFlags.requestBackgroundLocationPermission(); } catch {}
    // If after 1.5s nothing took us off-screen, the runtime prompt was
    // suppressed (user previously hit "Don't allow" twice) — fall back to
    // the app-info screen so they can still grant manually.
    setTimeout(async () => {
      document.removeEventListener('visibilitychange', onVis);
      if (stayedVisible) {
        try { await DeviceFlags.openAppLocationSettings(); } catch {}
      }
      void evaluate();
    }, 1500);
  }, [evaluate]);

  // ─── Lifecycle / listeners ──────────────────────────────────────────
  useEffect(() => {
    cancelledRef.current = false;
    isNativeRef.current = Capacitor.isNativePlatform();
    isAndroidRef.current = isNativeRef.current && Capacitor.getPlatform() === 'android';

    const cleanups: Array<() => void> = [];

    // Initial + cold-start retries
    void evaluate();
    const t1 = setTimeout(() => { if (!cancelledRef.current) void evaluate(); }, 0);
    const t2 = setTimeout(() => { if (!cancelledRef.current) void evaluate(); }, 800);
    cleanups.push(() => { clearTimeout(t1); clearTimeout(t2); });

    // Location toggle broadcast (Android)
    if (isAndroidRef.current) {
      const handle = DeviceFlags.addListener('locationStateChanged', () => {
        if (!cancelledRef.current) void evaluate();
      });
      cleanups.push(() => { try { handle.then(h => h.remove()); } catch {} });
    }

    // App lifecycle
    if (isNativeRef.current) {
      (async () => {
        try {
          const { App } = await import('@capacitor/app');
          const a = await App.addListener('appStateChange', ({ isActive }) => {
            if (isActive && !cancelledRef.current) void evaluate();
          });
          const b = await App.addListener('resume', () => {
            if (!cancelledRef.current) void evaluate();
          });
          cleanups.push(() => { try { a.remove(); } catch {} try { b.remove(); } catch {} });
        } catch {}
      })();
    } else {
      const onVis = () => { if (document.visibilityState === 'visible' && !cancelledRef.current) void evaluate(); };
      document.addEventListener('visibilitychange', onVis);
      cleanups.push(() => document.removeEventListener('visibilitychange', onVis));
    }

    // 5s revalidate (Battery / Developer have no broadcast)
    if (isAndroidRef.current) {
      pollRef.current = setInterval(() => {
        if (!cancelledRef.current) void evaluate();
      }, 5000);
      cleanups.push(() => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } });
    }

    return () => {
      cancelledRef.current = true;
      cleanups.forEach((fn) => { try { fn(); } catch {} });
    };
  }, [evaluate]);

  return (
    <PermissionsGateContext.Provider value={{ status, perms, request, revalidate: evaluate, locationStep, confirmBackgroundRationale }}>
      {children}
    </PermissionsGateContext.Provider>
  );
}